import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Compass, UserCircle, LogOut, LayoutDashboard, LogIn } from 'lucide-react';
import { SettingsProvider, useAppFont } from '../contexts/SettingsContext';
import { MosqueProvider, useMosque } from '../contexts/MosqueContext';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from './BottomNav';

import HomePage from '../pages/HomePage';
import JadwalPage from '../pages/JadwalPage';
import InfaqPage from '../pages/InfaqPage';
import PasarPage from '../pages/PasarPage';
import UMKMDetailPage from '../pages/UMKMDetailPage';
import TasbihPage from '../pages/TasbihPage';
import QuranPage from '../pages/QuranPage';
import StreamingPage from '../pages/StreamingPage';
import WartaPage from '../pages/WartaPage';
import AbsenPage from '../pages/AbsenPage';
import PengurusPage from '../pages/PengurusPage';
import ImamKhatibPage from '../pages/ImamKhatibPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Spinner = () => (
  <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-200 border-t-emerald-700" />
  </div>
);

const MosqueInner = () => {
  useAppFont();
  const location = useLocation();
  const navigate = useNavigate();
  const { mosque, to } = useMosque();
  const { user, logout, loginWithGoogle } = useAuth();
  const [platform, setPlatform] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const isDetail = /\/pasar\/[^/]+$/.test(location.pathname);

  useEffect(() => {
    axios.get(`${API_URL}/api/platform-settings`).then((r) => setPlatform(r.data)).catch(() => {});
  }, []);

  // Android/Mobile back button fix: prevent app exit, stay in mosque app
  useEffect(() => {
    // Push a duplicate history entry so first back press stays in app
    window.history.pushState(null, '', window.location.href);

    const onPopState = () => {
      if (!window.location.pathname.startsWith('/region/')) {
        // User backed out of mosque app – push back to current page
        window.history.pushState(null, '', window.location.href);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []); // Run once on mount

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const getDashboardPath = () => {
    if (!user) return null;
    const role = user.role;
    return role === 'super_admin' ? '/superadmin' :
           role === 'takmir' ? '/admin' :
           role === 'merchant' ? '/merchant' : '/jamaah';
  };

  const handleMasuk = () => {
    setDropdownOpen(false);
    localStorage.setItem('auth_return', location.pathname);
    navigate('/login');
  };

  const handleGoogleLogin = () => {
    setDropdownOpen(false);
    loginWithGoogle(location.pathname);
  };

  const handleLogout = async () => {
    setDropdownOpen(false);
    await logout();
  };

  // Dynamic PWA manifest + identity per mosque (unique icon/name + start_url isolation)
  useEffect(() => {
    if (!mosque) return;
    const href = `${API_URL}/api/manifest?regency=${mosque.regency_slug}&village=${mosque.village_slug}&mosque=${mosque.slug}`;
    let link = document.querySelector('link[rel="manifest"]');
    const prev = link ? link.getAttribute('href') : null;
    if (!link) { link = document.createElement('link'); link.rel = 'manifest'; document.head.appendChild(link); }
    link.setAttribute('href', href);
    const appleTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = appleTitle ? appleTitle.getAttribute('content') : null;
    if (appleTitle) appleTitle.setAttribute('content', mosque.name);
    return () => {
      if (link && prev) link.setAttribute('href', prev);
      if (appleTitle && prevTitle) appleTitle.setAttribute('content', prevTitle);
    };
  }, [mosque]);

  return (
    <div className="max-w-md mx-auto shadow-2xl min-h-screen bg-[#F8F9FA] relative" data-testid="mosque-app-shell">
      {/* Floating Top Bar: Account Icon only (X button removed per user request) */}
      <div className="fixed top-0 z-50 w-full max-w-md left-1/2 -translate-x-1/2 px-4 pt-4 flex justify-end items-start pointer-events-none">

        {/* Account Icon Dropdown */}
        <div className="relative pointer-events-auto" ref={dropdownRef}>
          <button
            data-testid="mosque-account-btn"
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/60 transition-colors overflow-hidden"
            title="Akun saya"
          >
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-full h-full object-cover rounded-full" />
            ) : (
              <UserCircle className="w-5 h-5" />
            )}
          </button>

          {dropdownOpen && (
            <div className="absolute right-0 top-11 bg-white rounded-2xl shadow-2xl border border-stone-100 w-52 overflow-hidden animate-in fade-in slide-in-from-top-2">
              {user ? (
                <>
                  <div className="px-4 py-3 bg-emerald-50 border-b border-stone-100">
                    <p className="text-sm font-semibold text-emerald-900 truncate">{user.name}</p>
                    <p className="text-xs text-emerald-700 truncate">{user.email}</p>
                    <span className="inline-block mt-1 text-[10px] bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 font-medium capitalize">
                      {user.role === 'takmir' ? 'Admin Masjid' : user.role === 'super_admin' ? 'Super Admin' : user.role === 'merchant' ? 'Merchant' : 'Jamaah'}
                    </span>
                  </div>
                  <button
                    data-testid="mosque-dashboard-btn"
                    onClick={() => { setDropdownOpen(false); navigate(getDashboardPath()); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    <LayoutDashboard className="w-4 h-4 text-emerald-600" />
                    Dasbor Saya
                  </button>
                  <button
                    data-testid="mosque-logout-btn"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-stone-100"
                  >
                    <LogOut className="w-4 h-4" />
                    Keluar
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="mosque-login-btn"
                    onClick={handleMasuk}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 transition-colors"
                  >
                    <LogIn className="w-4 h-4 text-emerald-600" />
                    Masuk dengan Email
                  </button>
                  <button
                    data-testid="mosque-google-login-btn"
                    onClick={handleGoogleLogin}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-stone-50 transition-colors border-t border-stone-100"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Masuk dengan Google
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <Routes>
        <Route index element={<HomePage />} />
        <Route path="jadwal" element={<JadwalPage />} />
        <Route path="infaq" element={<InfaqPage />} />
        <Route path="pasar" element={<PasarPage />} />
        <Route path="pasar/:id" element={<UMKMDetailPage />} />
        <Route path="tasbih" element={<TasbihPage />} />
        <Route path="quran" element={<QuranPage />} />
        <Route path="streaming" element={<StreamingPage />} />
        <Route path="warta" element={<WartaPage />} />
        <Route path="absen" element={<AbsenPage />} />
        <Route path="pengurus" element={<PengurusPage />} />
        <Route path="imam-khatib" element={<ImamKhatibPage />} />
      </Routes>
      {!isDetail && (
        <div className="px-6 pb-28 pt-4 text-center" data-testid="mosque-footer">
          {platform?.footer_enabled && (
            <a
              href={platform.footer_url || '#'}
              target="_blank"
              rel="noreferrer"
              data-testid="platform-footer-link"
              className="text-xs font-body text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              {platform.footer_text}
            </a>
          )}
        </div>
      )}
      {!isDetail && <BottomNav />}
    </div>
  );
};

const MosqueShell = () => {
  const { regencySlug, villageSlug, mosqueSlug } = useParams();
  const navigate = useNavigate();
  const [mosque, setMosque] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    setStatus('loading');
    axios
      .get(`${API_URL}/api/mosques/resolve`, {
        params: { regency: regencySlug, village: villageSlug, mosque: mosqueSlug },
      })
      .then((r) => { setMosque(r.data); setStatus('ok'); })
      .catch(() => setStatus('notfound'));
  }, [regencySlug, villageSlug, mosqueSlug]);

  if (status === 'loading') return <Spinner />;

  if (status === 'notfound') {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex flex-col items-center justify-center px-6 text-center" data-testid="mosque-notfound">
        <Compass className="w-16 h-16 text-stone-300 mb-4" />
        <h1 className="text-2xl font-heading font-bold text-primary-dark mb-2">Masjid tidak ditemukan</h1>
        <p className="text-sm font-body text-text-secondary mb-6">
          Alamat masjid yang Anda tuju tidak tersedia atau belum aktif.
        </p>
        <button
          onClick={() => navigate('/')}
          className="bg-emerald-700 hover:bg-emerald-800 text-white font-body font-medium px-6 py-3 rounded-full transition-colors"
          data-testid="mosque-notfound-home-btn"
        >
          Cari Masjid Lain
        </button>
      </div>
    );
  }

  return (
    <SettingsProvider mosqueId={mosque.id} initialSettings={{ mosque_name: mosque.name, logo_url: mosque.logo_url, location_city: (mosque.regency_name || '').replace(/^Kota\s+/, '').replace(/^Kabupaten\s+/, '') || mosque.regency_name, location_address: mosque.address }}>
      <MosqueProvider mosque={mosque}>
        <MosqueInner />
      </MosqueProvider>
    </SettingsProvider>
  );
};

export default MosqueShell;
